import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import { Job } from '@app/models';

const BASE_URL = 'https://www.eduhealthcare.com';

@Injectable({ providedIn: 'root' })
export class SeoService {

  constructor(
    private titleService: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}


  setJobSeo(job: Job): void {
    if (!job) return;

    const cityDisplay = this.titleCase(job.city);
    const stateDisplay = this.titleCase(job.state);
    const title = `${job.name} in ${cityDisplay}, ${stateDisplay} | EDU Healthcare`;
    const description = this.truncate(this.stripHtml(job.short_job_description || job.long_job_description || ''), 160);

    const professionPart = job.urlParts?.profession || (job.profession ? job.profession.replace(/\s+/g, '-').toLowerCase() : 'unknown');
    const statePart = job.urlParts?.state || (job.state ? job.state.toLowerCase() : 'unknown');
    const cityPart = job.urlParts?.city || (job.city ? job.city.toLowerCase() : 'unknown');
    const canonicalUrl = `${BASE_URL}/jobs/${professionPart}/${statePart}/${cityPart}/${job.job_id}`;

    this.setTitle(title);
    this.setMetaDescription(description);
    this.setCanonicalUrl(canonicalUrl);
    this.setOpenGraphTags({ title, description, url: canonicalUrl });
    // JSON-LD is handled server-side by crawler-prerender middleware
    // Do NOT inject or remove JSON-LD here — bots get it from the prerender
  }


  setLandingPageSeo(config: { title: string; description: string; canonicalUrl: string }): void {
    this.setTitle(config.title);
    this.setMetaDescription(config.description);
    this.setCanonicalUrl(config.canonicalUrl);
    this.setOpenGraphTags({ title: config.title, description: config.description, url: config.canonicalUrl });
  }


  resetSeo(): void {
    this.setTitle('EDU Healthcare - Healthcare & Education Staffing Jobs Nationwide');
    this.setMetaDescription('Find healthcare and education jobs nationwide with EDU Healthcare. Browse thousands of positions for therapists, nurses, teachers, and more.');
    this.removeCanonical();
  }


  private setTitle(title: string): void {
    this.titleService.setTitle(title);
  }


  private setMetaDescription(description: string): void {
    this.meta.updateTag({ name: 'description', content: description });
  }


  private setCanonicalUrl(url: string): void {
    this.removeCanonical();
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    this.document.head.appendChild(link);
  }


  private removeCanonical(): void {
    const existing = this.document.querySelector('link[rel="canonical"]');
    if (existing) existing.remove();
  }


  private setOpenGraphTags(config: { title: string; description: string; url: string }): void {
    this.meta.updateTag({ property: 'og:title', content: config.title });
    this.meta.updateTag({ property: 'og:description', content: config.description });
    this.meta.updateTag({ property: 'og:url', content: config.url });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'EDU Healthcare' });
    this.meta.updateTag({ property: 'og:image', content: `${BASE_URL}/assets/images/Edu_Logo2_1.png` });
  }


  private setJsonLd(data: object): void {
    this.removeJsonLd();
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'seo-json-ld';
    script.text = JSON.stringify(data);
    this.document.head.appendChild(script);
  }


  removeJsonLd(): void {
    // Remove client-side JSON-LD
    const existing = this.document.getElementById('seo-json-ld');
    if (existing) existing.remove();

    // Also remove any prerendered JSON-LD scripts (no id) to avoid duplicates
    const allJsonLd = this.document.querySelectorAll('script[type="application/ld+json"]');
    allJsonLd.forEach(el => el.remove());
  }


  private buildJobPostingJsonLd(job: Job, canonicalUrl: string): object {
    const description = this.stripHtml(job.long_job_description || job.short_job_description || job.name || '');

    const jsonLd: any = {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      'title': job.name || 'Healthcare Position',
      'description': description,
      'datePosted': job.updatedAt ? new Date(job.updatedAt).toISOString() : new Date().toISOString(),
      'employmentType': this.mapEmploymentType(job.job_type),
      'directApply': true,
      'url': canonicalUrl,
      'hiringOrganization': {
        '@type': 'Organization',
        'name': 'EDU Healthcare',
        'sameAs': BASE_URL,
        'logo': `${BASE_URL}/assets/images/Edu_Logo2_1.png`
      },
      'jobLocation': {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': job.city || '',
          'addressRegion': job.state || '',
          'postalCode': job.zip || '',
          'addressCountry': 'US'
        }
      },
      'industry': 'Healthcare / Education',
      'occupationalCategory': job.profession || ''
    };

    if (job.compensation_min || job.compensation_max) {
      const unitText = this.mapUnitText(job.compensation_interval);
      jsonLd.baseSalary = {
        '@type': 'MonetaryAmount',
        'currency': job.compensation_currency || 'USD',
        'value': {
          '@type': 'QuantitativeValue',
          'unitText': unitText
        } as any
      };
      if (job.compensation_min && job.compensation_max) {
        (jsonLd.baseSalary as any).value.minValue = job.compensation_min;
        (jsonLd.baseSalary as any).value.maxValue = job.compensation_max;
      } else {
        (jsonLd.baseSalary as any).value.value = job.compensation_min || job.compensation_max;
      }
    }

    if (job.location && job.location.latitude && job.location.longitude) {
      jsonLd.jobLocation.geo = {
        '@type': 'GeoCoordinates',
        'latitude': job.location.latitude,
        'longitude': job.location.longitude
      };
    }

    const relevantDate = (job as any).updatedAt || job.createdAt;
    if (relevantDate) {
      const validThrough = new Date(relevantDate);
      validThrough.setDate(validThrough.getDate() + 60);
      jsonLd.validThrough = validThrough.toISOString();
    }

    return jsonLd;
  }


  private mapUnitText(interval: string): string {
    if (!interval) return 'HOUR';
    const i = interval.toLowerCase();
    if (i.includes('hour')) return 'HOUR';
    if (i.includes('week')) return 'WEEK';
    if (i.includes('month')) return 'MONTH';
    if (i.includes('year') || i.includes('annual')) return 'YEAR';
    if (i.includes('day')) return 'DAY';
    return 'HOUR';
  }

  private mapEmploymentType(jobType: string): string {
    if (!jobType) return 'FULL_TIME';
    const t = jobType.toLowerCase();
    if (t.includes('travel')) return 'CONTRACTOR';
    if (t.includes('permanent') || t.includes('full')) return 'FULL_TIME';
    if (t.includes('part')) return 'PART_TIME';
    if (t.includes('per diem')) return 'PER_DIEM';
    if (t.includes('temp')) return 'TEMPORARY';
    return 'FULL_TIME';
  }


  private stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }


  private truncate(text: string, length: number): string {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length - 3) + '...';
  }


  private titleCase(str: string): string {
    if (!str) return '';
    return str.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
}